'use client';

import { useButton, type AriaButtonProps } from '@react-aria/button';
import { useLink, type AriaLinkOptions } from '@react-aria/link';
import { type ElementType, useRef, useImperativeHandle } from 'react';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../../types/component';
import { mergeProps } from '../../../utils/mergeProps';
import { tv } from '../../../utils/tv';
import { disableInteractions } from '../../utils/disableInteractions';
import { filterProps } from '../../utils/filterProps';
import {
  Interactable,
  type InteractableBaseProps,
} from '../interactable/Interactable';

interface PressableBaseProps extends Omit<InteractableBaseProps, 'isPressed'> {
  /**
   * Whether the element is in a loading state.
   * When true, disables press interactions and hover;
   * sets `aria-disabled="true"` and `data-loading`.
   * @default false
   */
  isLoading?: boolean;
}

type OmittedKeys = 'disabled';

const DEFAULT_ELEMENT_TYPE = 'button';
type DefaultElementType = typeof DEFAULT_ELEMENT_TYPE;

type PressableProps<RootComponent extends ElementType = DefaultElementType> =
  PolymorphicComponentProps<PressableBaseProps, RootComponent, OmittedKeys>;

type PressableComponent = PolymorphicComponent<
  PressableBaseProps,
  DefaultElementType,
  OmittedKeys
>;

const pressableStyles = tv({
  slots: {
    root: [
      'cursor-pointer',
      'select-none',
      'touch-manipulation',
      '[-webkit-tap-highlight-color:transparent]',
      'data-loading:cursor-progress',
      // Compound selector (0,2,0) ensures disabled cursor wins over loading cursor (0,1,0)
      'disabled:data-loading:cursor-not-allowed',
    ],
  },
})();

const Pressable: PressableComponent = (props) => {
  // Cast to ElementType variant so TypeScript allows comparison with any element string.
  const rawProps = props as PressableProps<ElementType>;
  const Root: ElementType = rawProps.component ?? DEFAULT_ELEMENT_TYPE;
  const href = (props as PressableProps<'a'>).href;

  // Link scenario: native <a> element, or a custom router component with href/to.
  const isLink =
    Root === 'a' ||
    (typeof Root !== 'string' && (href !== undefined || 'to' in props));

  const [localProps, buttonRaProps, linkRaProps] = filterProps(
    props as PressableProps,
    ['useButton', 'useLink']
  );

  const {
    disableFocus,
    disableHover,
    interactionColors,
    isLoading = false,
    focusWithin,
    ref,
    className,
    children,
    ...restLocalProps
  } = localProps;

  const { isDisabled = false, type = DEFAULT_ELEMENT_TYPE } = buttonRaProps;

  const disableReason = { isDisabled, isLoading };
  const filteredLocalProps = disableInteractions(restLocalProps, disableReason);

  const internalRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  useImperativeHandle(ref, () => internalRef.current as HTMLButtonElement, []);

  // Prevent form submit/reset during loading
  const resolvedType =
    isLoading && (type === 'submit' || type === 'reset') ? 'button' : type;

  // `isLink` is determined by Root's element type and href — stable for a given
  // component instance, so conditional hook calls are safe here.
  const buttonHook = isLink
    ? null
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useButton(
        {
          // Cast buttonRaProps to AriaButtonProps due to `formAction` type mismatch:
          // React 19 defines formAction as `string | ((formData: FormData) => void | Promise<void>)`
          // but React Aria's AriaButtonProps only accepts `string`.
          // This is a known limitation until React Aria updates its types.
          ...(buttonRaProps as AriaButtonProps),
          type: resolvedType,
          elementType: Root,
        },
        internalRef
      );

  // For custom router components (non-string Root), apply anchor semantics
  // so useLink sets role="link" when needed and activates on Enter only.
  const resolvedElementType = typeof Root === 'string' ? Root : 'a';
  const linkHook = isLink
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useLink(
        {
          ...(linkRaProps as AriaLinkOptions),
          elementType: resolvedElementType,
        },
        internalRef
      )
    : null;

  const hookProps = isLink ? linkHook!.linkProps : buttonHook!.buttonProps;
  const isPressed = isLink ? linkHook!.isPressed : buttonHook!.isPressed;
  const filteredHookProps = disableInteractions(hookProps, disableReason);

  return (
    <Interactable
      {...mergeProps(filteredLocalProps, filteredHookProps)}
      component={Root}
      isDisabled={isDisabled}
      isPressed={isPressed}
      // Disabled/loading always disables hover; otherwise defer to the explicit prop.
      disableHover={isDisabled || isLoading || (disableHover ?? false)}
      // Disabled always disables focus; otherwise defer to the explicit prop.
      disableFocus={isDisabled || (disableFocus ?? false)}
      interactionColors={interactionColors}
      focusWithin={focusWithin}
      ref={internalRef}
      {...(!isLink && {
        type: Root === DEFAULT_ELEMENT_TYPE ? resolvedType : undefined,
      })}
      {...(isLoading && {
        'aria-disabled': 'true' as const,
        'data-loading': true,
      })}
      className={pressableStyles.root({ class: className })}
    >
      {children}
    </Interactable>
  );
};

Pressable.displayName = 'Pressable';

export { Pressable, type PressableProps, type PressableBaseProps };
